import React from "react";
import { MdRestaurantMenu, MdStar } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Hotel } from "src/store/apis/Hotels";
import { formatNumber } from "src/utility";
import {LocationIcon, CorrectIcon} from "src/assets/Icons";
import {
  getMealTypeLabel,
  splitName,
  getRoomBlocks,
  getInclusionTags,
  getCancellationInfo,
  formatSupplementDescription,
} from "src/Modules/Hotels/utils/room";
import { getValues } from "src/utility/tree";
import { DERIVED_HOTEL_TREE } from "./constants";
import { track } from "@amplitude/analytics-browser";

export interface HotelCardProps {
  hotelData: Hotel;
  pointsFactor?: number;
  numberOfNights?: number;
  totalGuests?: number;
  onBookNow?: () => void;
}

const HotelCard: React.FC<HotelCardProps> = ({ hotelData, pointsFactor = 1, numberOfNights = 1, totalGuests, onBookNow }) => {
  const { t } = useTranslation();
  const {
    hotelName,
    address,
    image,
    currency,
    originalCurrency,
    bestUnit,
    totalFare,
    totalOriginalFare,
    starCount,
    ratingLabel,
    hasFacilities,
    displayedFacilities,
  } = getValues(hotelData, DERIVED_HOTEL_TREE);

  const pricePerNight = totalFare != null && numberOfNights > 1 ? Math.round(totalFare / numberOfNights) : null;

  return (
    <div
      onClick={() => {
        track("Hotel Selected", { ...hotelData });
        onBookNow?.();
      }}
      className="cursor-pointer border-border-minimal flex w-full max-w-full flex-col overflow-hidden rounded-lg border bg-background-default shadow-lg sm:flex-row"
    >
      <div className="relative h-48 w-full overflow-hidden sm:h-auto sm:w-56 sm:shrink-0 lg:w-80">
        {image ? (
          <img src={image} alt={hotelName} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="bg-background-minimal flex h-full w-full items-center justify-center text-xs text-font-minimal">
            {t("Hotels.common.noImage")}
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col md:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-4 p-5">
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 flex-col gap-1">
              <h3 className="text-font-default truncate text-lg font-bold leading-6">{hotelName}</h3>
              {address && (
                <div className="text-font-subtle flex min-w-0 items-center gap-1 text-sm">
                  <LocationIcon className="h-3 w-3 shrink-0" />
                  <span className="truncate">{address}</span>
                </div>
              )}
            </div>
            {starCount > 0 && (
              <div className="flex shrink-0 items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: starCount }, (_, i) => (
                    <MdStar key={i} className="h-3.5 w-3.5 text-icon-rating-star" />
                  ))}
                </div>
                {ratingLabel && (
                  <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-widest text-font-default">{ratingLabel}</span>
                )}
              </div>
            )}
          </div>

          {bestUnit &&
            (() => {
              const roomBlocks = getRoomBlocks(bestUnit);
              const combinedTitle = (bestUnit.roomTypeName ?? []).map((name: string) => splitName(name).title).join(" & ");
              const cancellation = getCancellationInfo(bestUnit.cancelPolicies, bestUnit.isRefundable);
              const inclusionTags = getInclusionTags(bestUnit);

              return (
                <div className="border-s-border-primary bg-background-minimal flex flex-1 flex-col rounded-lg border-s-4 px-4 py-3">
                  <h5 className="text-font-default m-0 mb-2 text-base font-bold leading-6">{combinedTitle}</h5>
                  {roomBlocks.map((block, blockIdx) => {
                    const { details } = splitName(block.name);

                    return (
                      <div key={blockIdx} className="mb-4 flex min-w-0 flex-1 flex-col gap-2 border-s-2 border-s-border-hover ps-4 last:mb-0">
                        {details && (
                          <div className="text-12 text-font-subtle flex flex-col text-left">
                            <p className="m-0 font-bold">{t("Hotels.common.roomDetails")}</p>
                            <p className="m-0 font-normal">{details}</p>
                          </div>
                        )}

                        <div className="mt-1 flex flex-wrap gap-2 text-xs font-bold">
                          <span
                            className={`inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5 ${
                              cancellation.refundable
                                ? "bg-background-success text-font-success"
                                : "bg-background-danger text-font-danger"
                            }`}
                          >
                            {cancellation.refundable ? t("Flights.listing.refundable") : t("Hotels.common.nonRefundable")}
                          </span>

                          {cancellation.hasFreeCancellation && (
                            <span className="inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-success px-3 py-1.5 text-font-success">
                              <CorrectIcon className="text-font-success h-4 w-4 shrink-0" />
                              {cancellation.freeCancelUntil
                                ? t("Hotels.common.freeCancellationUntil", { date: cancellation.freeCancelUntil })
                                : t("Hotels.common.freeCancellation")}
                            </span>
                          )}

                          {cancellation.paidPolicies.map((policy, i) => (
                            <span key={i} className="inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-danger px-3 py-1.5 text-font-danger">
                              {t(
                                policy.chargeType === "Percentage"
                                  ? "Hotels.common.cancellationChargePercentage"
                                  : "Hotels.common.cancellationChargeAmount",
                                {
                                  date: policy.fromDate,
                                  charge:
                                    policy.chargeType === "Percentage" ? policy.cancellationCharge : formatNumber(policy.cancellationCharge),
                                  currency: policy.currency ?? currency,
                                },
                              )}
                            </span>
                          ))}

                          {bestUnit.mealType && (
                            <span className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5">
                              <MdRestaurantMenu className="h-4 w-4" />
                              {getMealTypeLabel(bestUnit.mealType)}
                            </span>
                          )}

                          {block.promotion && (
                            <span className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5">
                              <CorrectIcon className="h-4 w-4 shrink-0" />
                              {block.promotion}
                            </span>
                          )}

                          {inclusionTags.map((tag, i) => (
                            <span
                              key={i}
                              className="text-font-informative inline-flex w-fit items-center gap-1.5 rounded-sm bg-background-informative px-3 py-1.5"
                            >
                              <CorrectIcon className="h-4 w-4 shrink-0" />
                              {tag}
                            </span>
                          ))}

                          {block.mandatorySupplements.length > 0 && (
                            <span className="text-font-warning bg-background-warning inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5">
                              {block.mandatorySupplements
                                .map(
                                  (s) =>
                                    `${formatSupplementDescription(s.description)}${s.price > 0 ? ` - ${t("Hotels.common.payAtHotel", { price: formatNumber(s.price), currency: s.currency ?? "" })}` : ""}`,
                                )
                                .join(" . ")}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
        </div>

        <div className="border-border-hover hidden w-px self-stretch border-l border-dashed md:block" />

        <div className="border-border-hover flex w-full max-w-full flex-col border-t border-dashed md:w-56 md:shrink-0 md:border-t-0 lg:w-64">
          <div className="flex h-full flex-col p-5">
            {hasFacilities && (
              <div className="border-border-hover mb-4 border-b border-dashed pb-4">
                <span className="mb-2 block text-xs font-normal uppercase tracking-widest text-font-minimal">
                  {t("Hotels.common.hotelFacilities")}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {displayedFacilities.map((facility: string, i: number) => (
                    <span key={i} className="bg-background-minimal text-font-subtle inline-flex items-center rounded px-2 py-1 text-xs font-bold">
                      {facility}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-auto flex flex-col items-start gap-1">
              {(numberOfNights > 0 || totalGuests) && (
                <span className="text-font-subtle text-xs font-medium">
                  {numberOfNights === 1 ? t("Hotels.common.oneNightLower") : t("Hotels.common.nightsCountLower", { count: numberOfNights })}
                  {totalGuests
                    ? `, ${totalGuests === 1 ? t("Hotels.common.oneGuest") : t("Hotels.common.guestsCount", { count: totalGuests })}`
                    : ""}
                </span>
              )}

              <div className="flex flex-col items-end justify-center gap-x-1.5 gap-y-0">
                <div className="flex items-end gap-x-1.5">
                  <span className="text-font-default text-base font-bold tracking-tight">
                    {formatNumber(totalFare!)} {currency}
                  </span>
                  {
                    <>
                      <span className="text-border-hover text-base font-normal">|</span>
                      <span className="text-font-subtle text-base font-medium">
                        {formatNumber(totalOriginalFare!)} {originalCurrency}
                      </span>
                    </>
                  }
                </div>

                <span className="text-font-active text-base font-bold">
                  {formatNumber(Math.round(totalFare! * pointsFactor))} {t("Flights.listing.pts")}
                </span>
              </div>

              {pricePerNight != null && (
                <span className="text-font-subtle text-xs">
                  {t("Hotels.common.pricePerNight", { amount: formatNumber(pricePerNight), currency })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelCard;
